/**
 * Create Batch Page
 * Allows manufacturers to create product batches with serial numbers and enhanced product information
 */

import { useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { confidentialService } from '../services/confidential.service';
import { integrationService } from '../services/integration.service';
import { productService, ProductBatch, BatchTitleOptions } from '../services/product.service';
import { enhancedProductService } from '../services/enhanced-product.service';
import { operationCostsService } from '../services/operation-costs.service';
import { formatBatchName } from '../lib/utils';
import { Button, Input, Alert, PageHeader } from '../components/ui';
import CreditValidationAlert from '../components/CreditValidationAlert';
import CreditBalanceWidget from '../components/CreditBalanceWidget';
import ImageUpload, { UploadedImage } from '../components/ImageUpload';
import NutritionInformationForm from '../components/NutritionInformationForm';
import CustomContentForm from '../components/CustomContentForm';
import ManufacturingDetailsForm from '../components/ManufacturingDetailsForm';
import PackagingHierarchyForm, { PackagingHierarchyData } from '../components/PackagingHierarchyForm';
import {
  BatchCreationStepHelp,
  batchCreationGuideSteps,
  type GuideStep,
} from '../components/BatchCreationGuideSection';
import { BatchTitleSettingsManager as BatchTitleSettingsSection } from './BatchTitleSettingsPage';
import { 
  NutritionInformation, 
  CustomProductContent, 
  ManufacturingDetails,
  EnhancedBatchTemplate,
  LabelDesign,
  ENHANCED_PRODUCT_INFO_CONSTANTS 
} from '../../../backend/src/types/enhanced-product-info.types';

type ConfidentialFormState = {
  retailerDiscount: string;
  retailerPromoCode: string;
  retailerInstruction: string;
  dealerDiscount: string;
  dealerPromoCode: string;
  dealerInstruction: string;
  validUntil: string;
  notes: string;
};

const defaultConfidentialForm: ConfidentialFormState = {
  retailerDiscount: '',
  retailerPromoCode: '',
  retailerInstruction: '',
  dealerDiscount: '',
  dealerPromoCode: '',
  dealerInstruction: '',
  validUntil: '',
  notes: '',
};

type ProduceNutritionPreset = {
  key: 'alphonso-mango' | 'strawberry';
  label: string;
  variety: string;
  summary: string;
  nutritionInfo: Partial<NutritionInformation>;
};

const getTodayYmd = () => new Date().toISOString().split('T')[0];

const produceNutritionPresets: ProduceNutritionPreset[] = [
  {
    key: 'alphonso-mango',
    label: 'Alphonso Mango',
    variety: 'Alphonso mango',
    summary: 'Approximate nutrition per 100 g with Vitamin C, Vitamin A, and potassium reference values.',
    nutritionInfo: {
      servingSize: '100 g',
      caloriesPerServing: 70,
      caloriesPer100g: 70,
      macronutrients: {
        protein: 0.6,
        carbohydrates: 17,
        totalFat: 0.3,
        fiber: 1.8,
        sugar: 15,
        sodium: 0,
      },
      vitamins: [
        { name: 'Vitamin C', amount: 32, unit: 'mg' },
        { name: 'Vitamin A', amount: 55, unit: 'mcg RAE' },
      ],
      minerals: [{ name: 'Potassium', amount: 160, unit: 'mg' }],
    },
  },
  {
    key: 'strawberry',
    label: 'Strawberry',
    variety: 'Strawberry',
    summary: 'Approximate nutrition per 100 g with Vitamin C, Vitamin A, and potassium reference values.',
    nutritionInfo: {
      servingSize: '100 g',
      caloriesPerServing: 32,
      caloriesPer100g: 32,
      macronutrients: {
        protein: 0.7,
        carbohydrates: 7.8,
        totalFat: 0.3,
        fiber: 2,
        sugar: 4.8,
        sodium: 0,
      },
      vitamins: [
        { name: 'Vitamin C', amount: 58, unit: 'mg' },
        { name: 'Vitamin A', amount: 1, unit: 'mcg RAE' },
      ],
      minerals: [{ name: 'Potassium', amount: 155, unit: 'mg' }],
    },
  },
];

const isNutritionInfoEmpty = (value: Partial<NutritionInformation>) => {
  const macros = value.macronutrients || {};
  return !(
    value.servingSize ||
    value.servingsPerContainer !== undefined ||
    value.caloriesPerServing !== undefined ||
    value.caloriesPer100g !== undefined ||
    Object.values(macros).some((entry) => entry !== undefined) ||
    (value.vitamins && value.vitamins.length > 0) ||
    (value.minerals && value.minerals.length > 0)
  );
};

const parseYmdDate = (value: string): Date | null => {
  if (!value.trim()) return null;
  const parsed = new Date(`${value}T00:00:00Z`);
  return Number.isNaN(parsed.getTime()) ? null : parsed;
};

const formatDateLabel = (value?: string | null): string => {
  if (!value) return 'Not set';
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return value;
  return parsed.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
};

const quickSetupHelpStep: GuideStep = {
  id: 'Prep',
  title: 'Start Faster With Reuse',
  summary:
    'Before typing anything, copy a previous batch setup or saved label design so only the new batch-specific fields stay manual.',
  bullets: [
    'Reuse batch configuration to prefill QR settings, nutrition, packaging, images, and label design.',
    'Product name and serial numbers stay blank so the new run still has its own identity.',
    'If only the thermal layout should carry forward, reuse just the saved label design.',
  ],
  exampleTitle: 'Example: Reuse flow',
  examplePoints: [
    'Reuse last week\'s mango batch to keep the same nutrition, images, and QR setup.',
    'Enter today\'s batch name, date, and serial range for the new run.',
    'Switch label design only when the pack layout changed.',
  ],
  primaryActionLabel: 'Open Existing Batch Selector',
  primaryActionTarget: 'batch-management',
};

const confidentialHelpStep: GuideStep = {
  id: 'Optional',
  title: 'Add Confidential Release Data (Optional)',
  summary:
    'Use this only when retailers or dealers should receive private instructions, promo codes, or discounts through confidential access.',
  bullets: [
    'Leave the full section blank if the batch does not need confidential retailer or dealer data.',
    'Retailer payload needs at least one retailer field when you decide to use it.',
    'Confidential premium access must be active before this data can be published.',
  ],
  exampleTitle: 'Example: Confidential payload',
  examplePoints: [
    'Retailer discount: 8% off',
    'Retailer promo code: RETAILER8',
    'Dealer instruction: Display only in premium cold-storage channels',
  ],
  primaryActionLabel: 'Open API Access',
  primaryActionPath: '/api-access',
};

export default function CreateBatchPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAuthStore();
  const isOperationManager = !!user?.isOperationManager;
  const canManageConfidential = user?.roleLevel === 0 && !isOperationManager;
  
  const [formData, setFormData] = useState({
    productName: '',
    productCode: '',
    unitCode: '',
    processVersion: '',
    eventDate: getTodayYmd(),
    serialStart: '',
    serialEnd: '',
    includeInnerQr: true
  });
  const [qrTokenFormat, setQrTokenFormat] = useState<'legacy' | 'short'>('legacy');
  const [qrTokenLength, setQrTokenLength] = useState(16);
  const [labelDesignOptions, setLabelDesignOptions] = useState<ProductBatch[]>([]);
  const [labelDesignSelection, setLabelDesignSelection] = useState('');
  const [labelDesign, setLabelDesign] = useState<LabelDesign | undefined>(undefined);
  const [labelDesignLoading, setLabelDesignLoading] = useState(false);
  const [templateOptions, setTemplateOptions] = useState<ProductBatch[]>([]);
  const [templateSelection, setTemplateSelection] = useState('');
  const [templateLoading, setTemplateLoading] = useState(false);
  const [templateApplying, setTemplateApplying] = useState(false);
  const [templateError, setTemplateError] = useState<string | null>(null);
  const [batchTitleOptions, setBatchTitleOptions] = useState<BatchTitleOptions | null>(null);
  const [batchTitleLoading, setBatchTitleLoading] = useState(false);
  const [batchTitleError, setBatchTitleError] = useState<string | null>(null);
  const [batchSearchQuery, setBatchSearchQuery] = useState('');
  const [selectedExistingBatchId, setSelectedExistingBatchId] = useState('');

  // Enhanced product information state
  const [productImages, setProductImages] = useState<UploadedImage[]>([]);
  const [productType, setProductType] = useState<'manufactured' | 'produce'>('manufactured');
  const [nutritionInfo, setNutritionInfo] = useState<Partial<NutritionInformation>>({});
  const [appliedProducePresetKey, setAppliedProducePresetKey] = useState<ProduceNutritionPreset['key'] | null>(null);
  const [customContent, setCustomContent] = useState<Partial<CustomProductContent>>({});
  const [produceDetails, setProduceDetails] = useState<{ harvestDate?: string; origin?: string; variety?: string; storageInstructions?: string; expiryDate?: string; bestBeforeDays?: number }>({});
  const [manufacturingDetails, setManufacturingDetails] = useState<Partial<ManufacturingDetails>>({});
  const [packagingData, setPackagingData] = useState<Partial<PackagingHierarchyData>>({});
  const [showEnhancedSections, setShowEnhancedSections] = useState(false);
  const [confidentialForm, setConfidentialForm] = useState<ConfidentialFormState>(defaultConfidentialForm);
  const [confidentialAccessActive, setConfidentialAccessActive] = useState(false);
  const [confidentialStatusLoading, setConfidentialStatusLoading] = useState(false);
  const [confidentialStatusError, setConfidentialStatusError] = useState<string | null>(null);
  
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [validationErrors, setValidationErrors] = useState<{ [key: string]: string }>({});
  const [hasCredits, setHasCredits] = useState(false);
  const [creditDetails, setCreditDetails] = useState<any>(null);
  const [costCalculation, setCostCalculation] = useState<{
    perUnitCost: number;
    totalCost: number;
    currency: string;
  } | null>(null);
  const [loadingCost, setLoadingCost] = useState(false);
  const [costError, setCostError] = useState<string | null>(null);

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    
    // Clear validation error for this field
    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData(prev => ({ ...prev, [name]: checked }));
  };

  const handleSelectChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));

    if (validationErrors[name]) {
      setValidationErrors(prev => {
        const newErrors = { ...prev };
        delete newErrors[name];
        return newErrors;
      });
    }
  };

  const handleNutritionChange = (field: keyof NutritionInformation, value: any) => {
    setAppliedProducePresetKey(null);
    setNutritionInfo(prev => ({ ...prev, [field]: value }));
  };

  const handleCustomContentChange = (field: keyof CustomProductContent, value: any) => {
    setCustomContent(prev => ({ ...prev, [field]: value }));
  };

  const handleProduceDetailsChange = (field: keyof typeof produceDetails, value: any) => {
    setProduceDetails(prev => ({ ...prev, [field]: value }));
  };

  const handleManufacturingDetailsChange = (field: keyof ManufacturingDetails, value: any) => {
    setManufacturingDetails(prev => ({ ...prev, [field]: value }));
  };

  const handlePackagingDataChange = (field: keyof PackagingHierarchyData, value: number) => {
    setPackagingData(prev => ({ ...prev, [field]: value }));
  };

  const handleProductTypeChange = (nextType: 'manufactured' | 'produce') => {
    setProductType(nextType);
    if (nextType === 'manufactured') {
      setAppliedProducePresetKey(null);
      return;
    }

    setProduceDetails((prev) => ({
      ...prev,
      harvestDate: prev.harvestDate || formData.eventDate || getTodayYmd(),
    }));
  };

  const applyProducePreset = (preset: ProduceNutritionPreset) => {
    setShowEnhancedSections(true);
    setProductType('produce');
    setProduceDetails((prev) => ({
      ...prev,
      variety: preset.variety,
      harvestDate: prev.harvestDate || formData.eventDate || getTodayYmd(),
    }));
    setNutritionInfo({
      ...preset.nutritionInfo,
    });
    setAppliedProducePresetKey(preset.key);
  };

  const handleConfidentialChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    const { name, value } = e.target;
    setConfidentialForm(prev => ({ ...prev, [name]: value }));

    if (
      validationErrors[name] ||
      validationErrors.confidential ||
      validationErrors.confidentialRetailer ||
      validationErrors.confidentialValidUntil
    ) {
      setValidationErrors(prev => {
        const next = { ...prev };
        delete next[name];
        delete next.confidential;
        delete next.confidentialRetailer;
        delete next.confidentialValidUntil;
        return next;
      });
    }
  };

  const handleLabelDesignSelect = (batchId: string) => {
    setLabelDesignSelection(batchId);
    if (!batchId) {
      setLabelDesign(undefined);
      return;
    }
    const selected = labelDesignOptions.find(batch => batch.id === batchId);
    setLabelDesign(selected?.labelDesign || undefined);
  };

  const replaceProductImages = (nextImages: UploadedImage[]) => {
    setProductImages(prev => {
      prev.forEach((image) => URL.revokeObjectURL(image.preview));
      return nextImages;
    });
  };

  const parseDateValue = (value: unknown): Date | undefined => {
    if (!value) return undefined;
    if (value instanceof Date) return value;
    const parsed = new Date(value as string);
    return Number.isNaN(parsed.getTime()) ? undefined : parsed;
  };

  const normalizeDateString = (value: unknown): string | undefined => {
    if (!value) return undefined;
    if (typeof value === 'string') {
      return value.split('T')[0];
    }
    if (value instanceof Date) {
      return value.toISOString().split('T')[0];
    }
    const parsed = new Date(String(value));
    return Number.isNaN(parsed.getTime()) ? undefined : parsed.toISOString().split('T')[0];
  };

  const toNumberOrUndefined = (value: unknown): number | undefined => {
    if (value === null || value === undefined || value === '') return undefined;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : undefined;
  };

  const downloadTemplateImage = async (
    image: EnhancedBatchTemplate['images'][number],
    index: number
  ): Promise<File | null> => {
    const sourceUrl = image.downloadUrl || image.optimizedUrl || image.thumbnailUrl;
    if (!sourceUrl) return null;

    const response = await fetch(sourceUrl);
    if (!response.ok) {
      throw new Error(`Failed to download image ${image.originalName || index + 1}`);
    }
    const blob = await response.blob();
    if (!blob.size) {
      throw new Error(`Empty image ${image.originalName || index + 1}`);
    }
    const fileType = blob.type || image.mimeType || 'image/jpeg';
    const fileName = image.originalName || `image-${index + 1}.jpg`;
    return new File([blob], fileName, { type: fileType });
  };

  const buildUploadedImagesFromTemplate = async (
    images: EnhancedBatchTemplate['images']
  ): Promise<{ uploadedImages: UploadedImage[]; failedCount: number }> => {
    const imagesToLoad = images.slice(0, ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_FILES);
    const results = await Promise.allSettled(
      imagesToLoad.map((image, index) => downloadTemplateImage(image, index))
    );

    const uploadedImages: UploadedImage[] = [];
    let failedCount = 0;

    results.forEach((result, index) => {
      if (result.status === 'fulfilled' && result.value) {
        const file = result.value;
        uploadedImages.push({
          id: `template-${imagesToLoad[index].id || index}`,
          file,
          preview: URL.createObjectURL(file),
          name: file.name,
          size: file.size,
          status: 'success'
        });
      } else {
        failedCount += 1;
      }
    });

    return { uploadedImages, failedCount };
  };

  const handleTemplateSelect = async (batchId: string) => {
    setTemplateSelection(batchId);
    setTemplateError(null);
    setAppliedProducePresetKey(null);

    if (!batchId) {
      return;
    }

    setTemplateApplying(true);

    try {
      const template = await enhancedProductService.getBatchTemplate(batchId);

      if (!template) {
        setTemplateError('No saved configuration found for this batch.');
        return;
      }

      setShowEnhancedSections(true);
      setProductType(template.productType || 'manufactured');
      if (template.qrTokenFormat) {
        setQrTokenFormat(template.qrTokenFormat);
        if (template.qrTokenFormat === 'short') {
          setQrTokenLength(template.qrTokenLength || 16);
        }
      }
      setNutritionInfo(template.nutritionInfo || {});
      setCustomContent(template.customContent || {});

      const nextProduceDetails = template.produceDetails
        ? {
            harvestDate: normalizeDateString(template.produceDetails.harvestDate),
            origin: template.produceDetails.origin || undefined,
            variety: template.produceDetails.variety || undefined,
            storageInstructions: template.produceDetails.storageInstructions || undefined,
            expiryDate: normalizeDateString(template.produceDetails.expiryDate),
            bestBeforeDays: toNumberOrUndefined(template.produceDetails.bestBeforeDays)
          }
        : {};
      setProduceDetails(nextProduceDetails);

      const nextManufacturingDetails = template.manufacturingDetails
        ? {
            manufacturingDate: parseDateValue(template.manufacturingDetails.manufacturingDate),
            expiryDate: parseDateValue(template.manufacturingDetails.expiryDate),
            batchNumber: template.manufacturingDetails.batchNumber || undefined,
            facilityLocation: template.manufacturingDetails.facilityLocation || undefined,
            mrp: toNumberOrUndefined(template.manufacturingDetails.mrp)
          }
        : {};
      setManufacturingDetails(nextManufacturingDetails);

      const nextPackagingData = template.packagingData
        ? {
            unitsPerCase: toNumberOrUndefined(template.packagingData.unitsPerCase),
            casesPerPallet: toNumberOrUndefined(template.packagingData.casesPerPallet),
            caseDimensions: template.packagingData.caseDimensions || undefined,
            palletDimensions: template.packagingData.palletDimensions || undefined
          }
        : {};
      setPackagingData(nextPackagingData);

      setLabelDesign(template.labelDesign || undefined);
      setLabelDesignSelection(template.labelDesign ? batchId : '');

      if (template.images && template.images.length > 0) {
        const { uploadedImages, failedCount } = await buildUploadedImagesFromTemplate(template.images);
        replaceProductImages(uploadedImages);
        if (failedCount > 0) {
          setTemplateError(`Failed to load ${failedCount} image${failedCount > 1 ? 's' : ''}. You can re-upload them.`);
        }
      } else {
        replaceProductImages([]);
      }

      setValidationErrors({});
    } catch (err) {
      console.error('Failed to apply batch template:', err);
      setTemplateError('Failed to load batch configuration. Please try again.');
    } finally {
      setTemplateApplying(false);
    }
  };

  const handleImageUpload = async (files: File[]) => {
    // Convert files to UploadedImage format for preview
    const newImages: UploadedImage[] = files.map((file, index) => ({
      id: `temp-${Date.now()}-${index}`,
      file,
      preview: URL.createObjectURL(file),
      name: file.name,
      size: file.size,
      status: 'success'
    }));

    setProductImages(prev => [...prev, ...newImages]);
  };

  const handleImageRemove = (imageId: string) => {
    setProductImages(prev => {
      const imageToRemove = prev.find(img => img.id === imageId);
      if (imageToRemove) {
        URL.revokeObjectURL(imageToRemove.preview);
      }
      return prev.filter(img => img.id !== imageId);
    });
  };

  const validate = (): boolean => {
    const errors: { [key: string]: string } = {};
    const hasRetailerConfidentialData =
      confidentialForm.retailerDiscount.trim().length > 0 ||
      confidentialForm.retailerPromoCode.trim().length > 0 ||
      confidentialForm.retailerInstruction.trim().length > 0;
    const hasDealerConfidentialData =
      confidentialForm.dealerDiscount.trim().length > 0 ||
      confidentialForm.dealerPromoCode.trim().length > 0 ||
      confidentialForm.dealerInstruction.trim().length > 0;
    const hasConfidentialData =
      hasRetailerConfidentialData ||
      hasDealerConfidentialData ||
      confidentialForm.validUntil.trim().length > 0 ||
      confidentialForm.notes.trim().length > 0;

    if (!formData.productName.trim()) {
      errors.productName = 'Product name is required';
    }

    if (!formData.productCode.trim()) {
      errors.productCode = 'Product code is required';
    }

    if (!formData.unitCode.trim()) {
      errors.unitCode = 'Unit code is required';
    }

    if (!formData.eventDate) {
      errors.eventDate = 'Batch date is required';
    } else {
      const eventDate = new Date(`${formData.eventDate}T00:00:00Z`);
      const today = new Date();
      const todayUtc = new Date(Date.UTC(today.getUTCFullYear(), today.getUTCMonth(), today.getUTCDate()));
      if (Number.isNaN(eventDate.getTime())) {
        errors.eventDate = 'Batch date must be a valid date';
      } else if (eventDate > todayUtc) {
        errors.eventDate = 'Batch date cannot be in the future';
      }
    }

    if (!formData.serialStart) {
      errors.serialStart = 'Starting serial number is required';
    } else if (parseInt(formData.serialStart) < 1) {
      errors.serialStart = 'Starting serial number must be at least 1';
    }

    if (!formData.serialEnd) {
      errors.serialEnd = 'Ending serial number is required';
    } else if (parseInt(formData.serialEnd) < 1) {
      errors.serialEnd = 'Ending serial number must be at least 1';
    }

    if (formData.serialStart && formData.serialEnd) {
      const start = parseInt(formData.serialStart);
      const end = parseInt(formData.serialEnd);
      
      if (end < start) {
        errors.serialEnd = 'Ending serial number must be greater than or equal to starting serial number';
      }

      const count = end - start + 1;
      if (count > 10000) {
        errors.serialEnd = 'Maximum 10,000 products per batch';
      }
    }

    // Validate enhanced product information if provided
    if (showEnhancedSections) {
      // Validate nutrition information
      if (!nutritionInfo.servingSize || nutritionInfo.servingSize.trim().length === 0) {
        errors.servingSize = 'Serving size is required';
      } else if (nutritionInfo.servingSize.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_SERVING_SIZE_LENGTH) {
        errors.servingSize = `Serving size must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_SERVING_SIZE_LENGTH} characters`;
      }

      if (nutritionInfo.caloriesPerServing && (nutritionInfo.caloriesPerServing < 0 || nutritionInfo.caloriesPerServing > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CALORIE_VALUE)) {
        errors.caloriesPerServing = `Calories must be between 0 and ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CALORIE_VALUE}`;
      }

      // Validate custom content lengths
      if (customContent.customMessage && customContent.customMessage.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH) {
        errors.customMessage = `Custom message must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_CUSTOM_MESSAGE_LENGTH} characters`;
      }

      if (customContent.ingredients && customContent.ingredients.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH) {
        errors.ingredients = `Ingredients list must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INGREDIENTS_LENGTH} characters`;
      }

      if (customContent.usageInstructions && customContent.usageInstructions.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH) {
        errors.usageInstructions = `Usage instructions must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_INSTRUCTIONS_LENGTH} characters`;
      }

      if (customContent.brandStory && customContent.brandStory.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH) {
        errors.brandStory = `Brand story must be less than ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_BRAND_STORY_LENGTH} characters`;
      }

      // Validate image count
      if (productImages.length > ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_FILES) {
        errors.images = `Maximum ${ENHANCED_PRODUCT_INFO_CONSTANTS.MAX_FILES} images allowed`;
      }

      // Validate packaging data if provided
      if (packagingData.unitsPerCase && packagingData.unitsPerCase < 1) {
        errors.unitsPerCase = 'Units per case must be at least 1';
      }

      if (packagingData.casesPerPallet && packagingData.casesPerPallet < 1) {
        errors.casesPerPallet = 'Cases per pallet must be at least 1';
      }
    }

    // Validate sufficient credits for calculated cost
    if (costCalculation && creditDetails) {
      if (creditDetails.available < costCalculation.totalCost) {
        errors.credits = `Insufficient credits. Need ${operationCostsService.formatCurrency(costCalculation.totalCost - creditDetails.available)} more.`;
      }
    }

    if (hasConfidentialData) {
      if (!canManageConfidential) {
        errors.confidential = 'Confidential data can only be published by a manufacturer admin.';
      } else if (confidentialStatusLoading) {
        errors.confidential = 'Confidential access status is still loading. Wait a moment and try again.';
      } else if (!confidentialAccessActive) {
        errors.confidential = 'Confidential premium access is not active. Activate it from API Access.';
      }

      if (!hasRetailerConfidentialData) {
        errors.confidentialRetailer = 'Add at least one retailer confidential field when using this section.';
      }

      if (confidentialForm.validUntil.trim() && !parseYmdDate(confidentialForm.validUntil)) {
        errors.confidentialValidUntil = 'Valid until must be a valid YYYY-MM-DD date.';
      }
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const calculateProductCount = (): number => {
    if (formData.serialStart && formData.serialEnd) {
      const start = parseInt(formData.serialStart);
      const end = parseInt(formData.serialEnd);
      if (!isNaN(start) && !isNaN(end) && end >= start) {
        return end - start + 1;
      }
    }
    return 0;
  };

  useEffect(() => {
    if (!user?.manufacturerId) return;

    setLabelDesignLoading(true);
    setTemplateLoading(true);
    setTemplateError(null);
    setBatchTitleLoading(true);
    setBatchTitleError(null);

    Promise.allSettled([
      productService.getManufacturerBatches(user.manufacturerId, 200),
      productService.getBatchTitleOptions()
    ])
      .then(([batchesResult, optionsResult]) => {
        if (batchesResult.status === 'fulfilled') {
          const batches = batchesResult.value;
          setTemplateOptions(batches);
          const withDesign = batches.filter(batch => batch.labelDesign);
          setLabelDesignOptions(withDesign);
        } else {
          console.error('Failed to load label designs:', batchesResult.reason);
          setTemplateError('Failed to load saved configurations.');
        }

        if (optionsResult.status === 'fulfilled') {
          setBatchTitleOptions(optionsResult.value);
        } else {
          console.error('Failed to load batch title options:', optionsResult.reason);
          setBatchTitleError('Failed to load batch title options.');
        }
      })
      .finally(() => {
        setLabelDesignLoading(false);
        setTemplateLoading(false);
        setBatchTitleLoading(false);
      });
  }, [user?.manufacturerId]);

  useEffect(() => {
    if (!user?.manufacturerId || !canManageConfidential) {
      setConfidentialAccessActive(false);
      setConfidentialStatusLoading(false);
      setConfidentialStatusError(null);
      return;
    }

    setConfidentialStatusLoading(true);
    setConfidentialStatusError(null);

    integrationService
      .getConfidentialAccessStatus()
      .then((result) => {
        setConfidentialAccessActive(Boolean(result.active));
      })
      .catch((err: any) => {
        setConfidentialAccessActive(false);
        setConfidentialStatusError(
          err.response?.data?.error || 'Failed to load confidential access status.'
        );
      })
      .finally(() => {
        setConfidentialStatusLoading(false);
      });
  }, [user?.manufacturerId, canManageConfidential]);

  // Calculate cost when quantity changes
  useEffect(() => {
    const productCount = calculateProductCount();
    
    if (productCount > 0) {
      setLoadingCost(true);
      setCostError(null);
      
      operationCostsService.calculateCost('batch_creation', productCount)
        .then((result) => {
          setCostCalculation({
            perUnitCost: result.perUnitCost,
            totalCost: result.totalCost,
            currency: result.currency,
          });
        })
        .catch((error) => {
          console.error('Failed to calculate cost:', error);
          setCostError('Failed to calculate cost. Please try again.');
          // Set fallback cost calculation
          setCostCalculation({
            perUnitCost: 0.1,
            totalCost: productCount * 0.1,
            currency: 'INR',
          });
        })
        .finally(() => {
          setLoadingCost(false);
        });
    } else {
      setCostCalculation(null);
      setCostError(null);
    }
  }, [formData.serialStart, formData.serialEnd]);

  const sortedBatches = useMemo(
    () =>
      [...templateOptions].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
      ),
    [templateOptions]
  );

  const filteredBatches = useMemo(() => {
    const query = batchSearchQuery.trim().toLowerCase();
    if (!query) {
      return sortedBatches;
    }

    return sortedBatches.filter((batch) => {
      const batchName = formatBatchName(batch.productName, batch.createdAt, batch.batchTitle).toLowerCase();
      return (
        batchName.includes(query) ||
        batch.productName.toLowerCase().includes(query) ||
        (batch.productCode || '').toLowerCase().includes(query) ||
        (batch.unitCode || '').toLowerCase().includes(query) ||
        (batch.processVersion || '').toLowerCase().includes(query) ||
        String(batch.serialStart).includes(query) ||
        String(batch.serialEnd).includes(query)
      );
    });
  }, [batchSearchQuery, sortedBatches]);

  const totalUnitsAcrossBatches = useMemo(
    () =>
      sortedBatches.reduce((sum, batch) => sum + (batch.serialEnd - batch.serialStart + 1), 0),
    [sortedBatches]
  );

  const latestBatch = sortedBatches[0] || null;
  const selectedExistingBatch = useMemo(() => {
    if (batchSearchQuery.trim() && filteredBatches.length === 0) {
      return null;
    }

    return (
      filteredBatches.find((batch) => batch.id === selectedExistingBatchId) ||
      sortedBatches.find((batch) => batch.id === selectedExistingBatchId) ||
      filteredBatches[0] ||
      latestBatch
    );
  }, [batchSearchQuery, filteredBatches, latestBatch, selectedExistingBatchId, sortedBatches]);
  const createBatchGuideStep = batchCreationGuideSteps[1];
  const enhancedGuideStep = batchCreationGuideSteps[2];
  const creditsGuideStep = batchCreationGuideSteps[3];
  const postCreationGuideStep = batchCreationGuideSteps[4];
  const suggestedProducePreset = useMemo(() => {
    if (productType !== 'produce') {
      return null;
    }

    const normalizedValue = `${produceDetails.variety || ''} ${formData.productName || ''}`.trim().toLowerCase();
    if (!normalizedValue) {
      return null;
    }

    return (
      produceNutritionPresets.find((preset) => normalizedValue.includes(preset.variety.toLowerCase())) || null
    );
  }, [formData.productName, produceDetails.variety, productType]);

  const refreshBatchTitleOptions = async () => {
    try {
      setBatchTitleLoading(true);
      setBatchTitleError(null);
      const options = await productService.getBatchTitleOptions();
      setBatchTitleOptions(options);
    } catch (err: any) {
      console.error('Failed to refresh batch title options:', err);
      setBatchTitleError(err.response?.data?.error || 'Failed to load batch title options.');
    } finally {
      setBatchTitleLoading(false);
    }
  };

  const scrollToSection = (sectionId: string) => {
    if (typeof document === 'undefined') {
      return;
    }

    document.getElementById(sectionId)?.scrollIntoView({
      behavior: 'smooth',
      block: 'start',
    });
  };

  const scrollToBatchManagement = () => {
    scrollToSection('batch-management-section');
  };

  const scrollToBatchTitleSettings = () => {
    scrollToSection('batch-title-settings-section');
  };

  const scrollToCreateForm = () => {
    scrollToSection('create-new-batch-section');
  };

  useEffect(() => {
    if (sortedBatches.length === 0) {
      if (selectedExistingBatchId) {
        setSelectedExistingBatchId('');
      }
      return;
    }

    if (!selectedExistingBatchId) {
      setSelectedExistingBatchId(filteredBatches[0]?.id || sortedBatches[0].id);
      return;
    }

    if (!sortedBatches.some((batch) => batch.id === selectedExistingBatchId)) {
      setSelectedExistingBatchId(filteredBatches[0]?.id || sortedBatches[0].id);
      return;
    }

    if (
      batchSearchQuery.trim() &&
      filteredBatches.length > 0 &&
      !filteredBatches.some((batch) => batch.id === selectedExistingBatchId)
    ) {
      setSelectedExistingBatchId(filteredBatches[0].id);
    }
  }, [batchSearchQuery, filteredBatches, selectedExistingBatchId, sortedBatches]);

  useEffect(() => {
    if (location.hash === '#batch-title-settings-section') {
      window.setTimeout(() => scrollToBatchTitleSettings(), 0);
      return;
    }

    if (location.hash === '#batch-guide-section') {
      window.setTimeout(() => scrollToCreateForm(), 0);
      return;
    }

    if (location.hash === '#create-new-batch-section') {
      window.setTimeout(() => scrollToCreateForm(), 0);
    }
  }, [location.hash]);

  useEffect(() => {
    if (productType !== 'produce') {
      return;
    }

    setProduceDetails((prev) => {
      if (prev.harvestDate) {
        return prev;
      }

      return {
        ...prev,
        harvestDate: formData.eventDate || getTodayYmd(),
      };
    });
  }, [productType, formData.eventDate]);

  const hasRetailerConfidentialData =
    confidentialForm.retailerDiscount.trim().length > 0 ||
    confidentialForm.retailerPromoCode.trim().length > 0 ||
    confidentialForm.retailerInstruction.trim().length > 0;
  const hasDealerConfidentialData =
    confidentialForm.dealerDiscount.trim().length > 0 ||
    confidentialForm.dealerPromoCode.trim().length > 0 ||
    confidentialForm.dealerInstruction.trim().length > 0;
  const hasConfidentialData =
    hasRetailerConfidentialData ||
    hasDealerConfidentialData ||
    confidentialForm.validUntil.trim().length > 0 ||
    confidentialForm.notes.trim().length > 0;

  const publishConfidentialReleaseIfNeeded = async (batchId: string): Promise<string | null> => {
    if (!hasConfidentialData) {
      return null;
    }

    if (!canManageConfidential) {
      return 'Confidential data was entered but your role cannot publish it.';
    }

    if (!confidentialAccessActive) {
      return 'Confidential premium access is not active.';
    }

    const retailer = {
      discount: confidentialForm.retailerDiscount.trim() || undefined,
      promoCode: confidentialForm.retailerPromoCode.trim() || undefined,
      instruction: confidentialForm.retailerInstruction.trim() || undefined,
    };

    if (!retailer.discount && !retailer.promoCode && !retailer.instruction) {
      return 'At least one retailer confidential field is required.';
    }

    const dealer = {
      discount: confidentialForm.dealerDiscount.trim() || undefined,
      promoCode: confidentialForm.dealerPromoCode.trim() || undefined,
      instruction: confidentialForm.dealerInstruction.trim() || undefined,
    };

    let validUntil: string | undefined;
    const validUntilInput = confidentialForm.validUntil.trim();
    if (validUntilInput) {
      const parsed = parseYmdDate(validUntilInput);
      if (!parsed) {
        return 'Confidential valid until date must be a valid YYYY-MM-DD date.';
      }

      validUntil = new Date(
        Date.UTC(parsed.getUTCFullYear(), parsed.getUTCMonth(), parsed.getUTCDate(), 23, 59, 59)
      ).toISOString();
    }

    try {
      await confidentialService.createRelease(batchId, {
        retailer,
        dealer: dealer.discount || dealer.promoCode || dealer.instruction ? dealer : undefined,
        notes: confidentialForm.notes.trim() || undefined,
        validUntil,
      });
      return null;
    } catch (err: any) {
      return err.response?.data?.error || 'Failed to publish confidential release.';
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!validate()) {
      return;
    }

    if (!user?.manufacturerId) {
      setError('User is not associated with a manufacturer');
      return;
    }

    try {
      setCreating(true);
      setError(null);

      // Prepare enhanced batch data
      const hasProduceAttributes =
        productType === 'produce' && Object.keys(produceDetails).length > 0;
      const hasManufacturingAttributes =
        productType === 'manufactured' && Object.keys(manufacturingDetails).length > 0;
      const hasPackagingAttributes = Boolean(
        packagingData.unitsPerCase && packagingData.casesPerPallet
      );
      const packagingPayload = hasPackagingAttributes
        ? {
            unitsPerCase: packagingData.unitsPerCase as number,
            casesPerPallet: packagingData.casesPerPallet as number,
            caseDimensions: packagingData.caseDimensions,
            palletDimensions: packagingData.palletDimensions,
          }
        : undefined;
      const enhancedBatchData = {
        productName: formData.productName.trim(),
        productCode: formData.productCode.trim(),
        unitCode: formData.unitCode.trim(),
        eventDate: formData.eventDate,
        serialStart: parseInt(formData.serialStart),
        serialEnd: parseInt(formData.serialEnd),
        productType,
        includeInnerQr: !!formData.includeInnerQr,
        qrTokenFormat,
        qrTokenLength: qrTokenFormat === 'short' ? qrTokenLength : undefined,
        labelDesign,
        processVersion: formData.processVersion.trim() || undefined,
        productImages: productImages.map(img => img.file),
        nutritionInfo: Object.keys(nutritionInfo).length > 0 ? nutritionInfo : undefined,
        customContent: Object.keys(customContent).length > 0 ? customContent : undefined,
        produceDetails: hasProduceAttributes ? produceDetails : undefined,
        manufacturingDetails: hasManufacturingAttributes
          ? manufacturingDetails
          : undefined,
        packagingData: packagingPayload
      };

      // Use enhanced product service if enhanced features are used
      const hasEnhancedFeatures =
        productType === 'produce' ||
        (showEnhancedSections &&
          (
            productImages.length > 0 ||
            Object.keys(nutritionInfo).length > 0 ||
            Object.keys(customContent).length > 0 ||
            hasProduceAttributes ||
            hasManufacturingAttributes ||
            hasPackagingAttributes
          ));

      let result;
      
      if (hasEnhancedFeatures) {
        result = await enhancedProductService.createEnhancedBatch(enhancedBatchData);
      } else {
        // Use regular product service for basic batches
        result = await productService.createBatch({
          manufacturerId: user.manufacturerId,
          productName: formData.productName.trim(),
          productCode: formData.productCode.trim(),
          unitCode: formData.unitCode.trim(),
          eventDate: formData.eventDate,
          serialStart: parseInt(formData.serialStart),
          serialEnd: parseInt(formData.serialEnd),
          qrTokenFormat,
          qrTokenLength: qrTokenFormat === 'short' ? qrTokenLength : undefined,
          labelDesign,
          processVersion: formData.processVersion.trim() || undefined,
          includeInnerQr: !!formData.includeInnerQr
        });
      }

      if (result.success && result.batch) {
        const batchId = 'batchId' in result.batch ? result.batch.batchId : result.batch.id;
        const confidentialPublishError = await publishConfidentialReleaseIfNeeded(batchId);
        const successPrefix = hasEnhancedFeatures ? 'Enhanced product batch' : 'Product batch';
        const successMessage = confidentialPublishError
          ? `${successPrefix} created. Confidential release was not published: ${confidentialPublishError}`
          : hasConfidentialData
            ? `${successPrefix} and confidential release created successfully!`
            : `${successPrefix} created successfully!`;

        navigate(`/products/batch/${batchId}`, {
          state: { message: successMessage }
        });
      } else {
        setError(result.error || 'Failed to create batch');
      }
    } catch (err: any) {
      console.error('Failed to create batch:', err);
      const apiErrors = err.response?.data?.errors;
      if (Array.isArray(apiErrors)) {
        const mapped: { [key: string]: string } = {};
        apiErrors.forEach((e: any) => {
          const rawField = (e.field || 'general').toString();
          const field = rawField.replace(/^\//, '').replace(/\//g, '.');
          mapped[field] = e.message || 'Invalid value';
        });
        setValidationErrors(mapped);
        setError(err.response?.data?.message || 'Please correct the highlighted fields.');
      } else {
        setError(err.response?.data?.error || 'Failed to create product batch');
      }
    } finally {
      setCreating(false);
    }
  };

  const handleCancel = () => {
    navigate('/products');
  };

  const productCount = calculateProductCount();

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-6">
          <PageHeader
            title="Batch Management"
            description="Review past batches, open batch operations, and create new batches from one workspace."
            actions={
              <div className="flex flex-wrap gap-2">
                <Button
                  onClick={scrollToCreateForm}
                  disabled={creating}
                >
                  Create New Batch
                </Button>
                <Button
                  variant="outline"
                  onClick={scrollToBatchTitleSettings}
                  disabled={creating}
                >
                  Batch Title Codes
                </Button>
                <Button
                  variant="outline"
                  onClick={() => navigate('/api-access')}
                  disabled={creating}
                >
                  API Access
                </Button>
                <Button
                  variant="outline"
                  onClick={handleCancel}
                  disabled={creating}
                >
                  Open Product Registry
                </Button>
              </div>
            }
          />
        </div>

        {/* Error Alert */}
        {error && (
          <Alert variant="error" className="mb-6">
            {error}
          </Alert>
        )}

        <section id="batch-management-section" className="rounded-xl border border-gray-200 bg-white p-6 shadow-sm mb-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
            <div>
              <h2 className="text-lg font-semibold text-gray-900">Existing Batches</h2>
              <p className="mt-1 text-sm text-gray-600">
                Batch Management keeps past batches accessible without taking over the page. Use the dropdown below to jump into an older batch when needed.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Total batches</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{sortedBatches.length}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Products minted</p>
                <p className="mt-2 text-2xl font-semibold text-gray-900">{totalUnitsAcrossBatches.toLocaleString()}</p>
              </div>
              <div className="rounded-lg border border-gray-200 bg-gray-50 px-4 py-3">
                <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Latest batch</p>
                <p className="mt-2 text-sm font-semibold text-gray-900">
                  {latestBatch
                    ? formatBatchName(latestBatch.productName, latestBatch.createdAt, latestBatch.batchTitle)
                    : 'No batches yet'}
                </p>
                {latestBatch ? (
                  <p className="mt-1 text-xs text-gray-500">{formatDateLabel(latestBatch.createdAt)}</p>
                ) : null}
              </div>
            </div>
          </div>

          <div className="mt-6 rounded-xl border border-gray-200 bg-gray-50 p-4">
            <div className="grid grid-cols-1 gap-4 xl:grid-cols-[minmax(0,0.8fr)_minmax(0,1.2fr)_auto] xl:items-end">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Filter existing batches
                </label>
                <Input
                  value={batchSearchQuery}
                  onChange={(e) => setBatchSearchQuery(e.target.value)}
                  placeholder="Search by product, code, title, or serial range..."
                  disabled={templateLoading}
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Open an existing batch
                </label>
                <select
                  value={batchSearchQuery.trim() && filteredBatches.length === 0 ? '' : selectedExistingBatchId}
                  onChange={(e) => setSelectedExistingBatchId(e.target.value)}
                  className="w-full rounded-lg border border-gray-300 bg-white px-4 py-2 text-gray-900 focus:border-transparent focus:ring-2 focus:ring-blue-500"
                  disabled={templateLoading || filteredBatches.length === 0}
                >
                  <option value="">
                    {sortedBatches.length === 0
                      ? 'No existing batches yet'
                      : filteredBatches.length === 0
                        ? 'No matching batches found'
                        : 'Select a batch'}
                  </option>
                  {filteredBatches.map((batch) => (
                    <option key={batch.id} value={batch.id}>
                      {formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)} (#{batch.serialStart}-{batch.serialEnd})
                    </option>
                  ))}
                </select>
                <p className="mt-2 text-xs text-gray-500">
                  {batchSearchQuery.trim()
                    ? `Showing ${filteredBatches.length} of ${sortedBatches.length} batches.`
                    : 'Use this dropdown to jump to a past batch without filling the page with cards.'}
                </p>
              </div>
              <div className="flex flex-wrap gap-2 xl:justify-end">
                <Button variant="outline" onClick={() => navigate('/products')}>
                  Product Registry
                </Button>
                <Button variant="outline" onClick={scrollToCreateForm} disabled={creating}>
                  New Batch Form
                </Button>
              </div>
            </div>

            {templateLoading ? (
              <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-white px-6 py-8 text-center text-sm text-gray-500">
                Loading existing batches...
              </div>
            ) : sortedBatches.length === 0 ? (
              <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-white px-6 py-8 text-center">
                <h3 className="text-lg font-medium text-gray-900">No batches created yet</h3>
                <p className="mt-2 text-sm text-gray-600">Create your first batch below.</p>
                <Button className="mt-4" onClick={scrollToCreateForm}>
                  Create First Batch
                </Button>
              </div>
            ) : batchSearchQuery.trim() && filteredBatches.length === 0 ? (
              <div className="mt-4 rounded-lg border border-dashed border-gray-200 bg-white px-6 py-8 text-center">
                <h3 className="text-lg font-medium text-gray-900">No matching batches found</h3>
                <p className="mt-2 text-sm text-gray-600">
                  Try a different search or clear the filter to see all saved batches.
                </p>
              </div>
            ) : selectedExistingBatch ? (
              <div className="mt-4 rounded-xl border border-gray-200 bg-white p-5 shadow-sm">
                <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
                  <div>
                    <h3 className="text-base font-semibold text-gray-900">
                      {formatBatchName(
                        selectedExistingBatch.productName,
                        selectedExistingBatch.createdAt,
                        selectedExistingBatch.batchTitle
                      )}
                    </h3>
                    <p className="mt-1 text-sm text-gray-600">
                      {selectedExistingBatch.productName}
                    </p>
                  </div>
                  <div className="rounded-full bg-gray-100 px-3 py-1 text-xs font-medium text-gray-700">
                    {(
                      selectedExistingBatch.serialEnd - selectedExistingBatch.serialStart + 1
                    ).toLocaleString()}{' '}
                    products
                  </div>
                </div>

                <div className="mt-4 grid grid-cols-1 gap-3 sm:grid-cols-2 xl:grid-cols-4">
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">
                      Serial range
                    </p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      #{selectedExistingBatch.serialStart} to #{selectedExistingBatch.serialEnd}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Created</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {formatDateLabel(selectedExistingBatch.createdAt)}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Codes</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {selectedExistingBatch.productCode || 'No product code'} /{' '}
                      {selectedExistingBatch.unitCode || 'No unit code'}
                    </p>
                  </div>
                  <div className="rounded-lg bg-gray-50 px-3 py-2">
                    <p className="text-xs font-medium uppercase tracking-wide text-gray-500">Batch date</p>
                    <p className="mt-1 text-sm font-medium text-gray-900">
                      {formatDateLabel(selectedExistingBatch.eventDate)}
                    </p>
                  </div>
                </div>

                <div className="mt-4 flex flex-wrap gap-2">
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/products/batch/${selectedExistingBatch.id}`)}
                  >
                    Open Batch
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/products/batch/${selectedExistingBatch.id}/manage`)}
                  >
                    Product Info
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => navigate(`/packaging/config/${selectedExistingBatch.id}`)}
                  >
                    Packaging
                  </Button>
                </div>
              </div>
            ) : null}
          </div>
        </section>

        <section id="batch-title-settings-section" className="mb-6 scroll-mt-24">
          <BatchCreationStepHelp
            step={batchCreationGuideSteps[0]}
            className="mb-4"
            onOpenBatchManagement={scrollToBatchManagement}
            onOpenBatchTitleSettings={scrollToBatchTitleSettings}
            onOpenCreateBatchForm={scrollToCreateForm}
          />
          <BatchTitleSettingsSection embedded onSettingsChanged={refreshBatchTitleOptions} />
        </section>

        {/* Credit Balance Widget */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
          <div className="lg:col-span-2">
            {/* Info Box */}
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex gap-3">
                <svg className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">About Product Batches</p>
                  <p>By default, each product receives two QR codes: an outer QR (pre-purchase verification) and an inner QR (post-purchase consumption marking). You can optionally disable inner QR below. You can create up to 10,000 products per batch.</p>
                  <p className="mt-2">
                    Confidential retailer and dealer release data can be added here during batch creation or later from the batch detail page.
                  </p>
                  <Button
                    type="button"
                    variant="outline"
                    className="mt-3"
                    onClick={() => navigate('/api-access')}
                    disabled={creating}
                  >
                    Open Confidential Settings
                  </Button>
                </div>
              </div>
            </div>
          </div>
          
          <div>
            <CreditBalanceWidget />
          </div>
        </div>

        <BatchCreationStepHelp
          step={creditsGuideStep}
          className="mb-6"
          onOpenBatchManagement={scrollToBatchManagement}
          onOpenBatchTitleSettings={scrollToBatchTitleSettings}
          onOpenCreateBatchForm={scrollToCreateForm}
        />

        {/* Credit Validation */}
        <CreditValidationAlert
          operationType="batch_creation"
          className="mb-6"
          onValidationChange={(isValid, details) => {
            setHasCredits(isValid);
            setCreditDetails(details);
          }}
        />

        {/* Form */}
        <form id="create-new-batch-section" onSubmit={handleSubmit} className="bg-white rounded-lg shadow p-6">
          <div className="space-y-6">
            <div className="border-b border-gray-200 pb-4">
              <h2 className="text-xl font-semibold text-gray-900">Create New Batch</h2>
              <p className="mt-1 text-sm text-gray-600">
                Use this form to mint a new product batch while keeping existing batch operations above on the same page.
              </p>
            </div>

            <BatchCreationStepHelp
              step={quickSetupHelpStep}
              onOpenBatchManagement={scrollToBatchManagement}
              onOpenBatchTitleSettings={scrollToBatchTitleSettings}
              onOpenCreateBatchForm={scrollToCreateForm}
            />

            <section className="rounded-xl border border-gray-200 bg-gray-50 p-4">
              <div>
                <h3 className="text-lg font-medium text-gray-900">Quick Setup</h3>
                <p className="mt-1 text-sm text-gray-600">
                  Reuse a previous batch configuration or a saved thermal label before you start entering this batch.
                </p>
              </div>

              <div className="mt-4 grid grid-cols-1 gap-4 xl:grid-cols-2">
                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h4 className="text-base font-medium text-gray-900">Reuse Batch Configuration</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Prefill images, QR settings, nutrition, packaging, and label design from a previous batch. Product name and serial numbers stay blank.
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Select a batch to reuse
                    </label>
                    <select
                      value={templateSelection}
                      onChange={(e) => handleTemplateSelect(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={templateLoading || templateApplying}
                    >
                      <option value="">Don't reuse a batch</option>
                      {templateOptions.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)} (#{batch.serialStart}-{batch.serialEnd})
                        </option>
                      ))}
                    </select>
                    {templateLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading saved configurations...</p>
                    )}
                    {templateApplying && (
                      <p className="text-xs text-blue-600 mt-2">Applying configuration...</p>
                    )}
                    {!templateLoading && templateOptions.length === 0 && (
                      <p className="text-xs text-gray-500 mt-2">No previous batches yet.</p>
                    )}
                    {templateError && (
                      <p className="text-xs text-red-600 mt-2">{templateError}</p>
                    )}
                  </div>
                </div>

                <div className="rounded-lg border border-gray-200 bg-white p-4">
                  <h4 className="text-base font-medium text-gray-900">Thermal Label Design</h4>
                  <p className="mt-1 text-sm text-gray-500">
                    Reuse a saved label layout from a previous batch.
                  </p>

                  <div className="mt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Reuse label design
                    </label>
                    <select
                      value={labelDesignSelection}
                      onChange={(e) => handleLabelDesignSelect(e.target.value)}
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={labelDesignLoading}
                    >
                      <option value="">Don't reuse a design</option>
                      {labelDesignOptions.map((batch) => (
                        <option key={batch.id} value={batch.id}>
                          {formatBatchName(batch.productName, batch.createdAt, batch.batchTitle)} (#{batch.serialStart}-{batch.serialEnd})
                        </option>
                      ))}
                    </select>
                    {labelDesignLoading && (
                      <p className="text-xs text-gray-500 mt-2">Loading saved designs...</p>
                    )}
                    {!labelDesignLoading && labelDesignOptions.length === 0 && (
                      <p className="text-xs text-gray-500 mt-2">No saved label designs yet.</p>
                    )}
                    {labelDesign && (
                      <p className="text-xs text-gray-600 mt-2">
                        Using: {labelDesign.brandName || 'Saved label design'} | QR{' '}
                        {(labelDesign.qrSizeInches ?? 0.75).toFixed(2)}"
                      </p>
                    )}
                  </div>
                </div>
              </div>
            </section>

            <section className="rounded-xl border border-gray-200 bg-white p-4">
              <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Product Type</h3>
                  <p className="mt-1 text-sm text-gray-600">
                    Choose this at the start so the batch follows the right flow for manufactured goods or fresh produce.
                  </p>
                </div>
                {productType === 'produce' && !showEnhancedSections ? (
                  <Button
                    type="button"
                    variant="outline"
                    size="sm"
                    onClick={() => setShowEnhancedSections(true)}
                    disabled={creating}
                  >
                    Open Produce Details
                  </Button>
                ) : null}
              </div>

              <div className="mt-4 flex flex-col gap-3 sm:flex-row">
                <button
                  type="button"
                  onClick={() => handleProductTypeChange('manufactured')}
                  className={`w-full sm:w-auto rounded-lg border px-4 py-2 text-sm font-medium ${
                    productType === 'manufactured'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700'
                  }`}
                  disabled={creating}
                >
                  Manufactured
                </button>
                <button
                  type="button"
                  onClick={() => handleProductTypeChange('produce')}
                  className={`w-full sm:w-auto rounded-lg border px-4 py-2 text-sm font-medium ${
                    productType === 'produce'
                      ? 'border-blue-600 bg-blue-50 text-blue-700'
                      : 'border-gray-300 text-gray-700'
                  }`}
                  disabled={creating}
                >
                  Fresh produce
                </button>
              </div>

              <div className="mt-3 rounded-lg border border-gray-200 bg-gray-50 px-4 py-3 text-sm text-gray-600">
                {productType === 'produce'
                  ? 'Fresh produce selected. Produce-specific fields stay available in Enhanced Product Information below.'
                  : 'Manufactured selected. Use Enhanced Product Information below only when you want to add nutrition, images, packaging, or manufacturing details.'}
              </div>
            </section>

            <BatchCreationStepHelp
              step={createBatchGuideStep}
              onOpenBatchManagement={scrollToBatchManagement}
              onOpenBatchTitleSettings={scrollToBatchTitleSettings}
              onOpenCreateBatchForm={scrollToCreateForm}
            />

            {/* Product Name */}
            <div>
              <label htmlFor="productName" className="block text-sm font-medium text-gray-700 mb-2">
                Product Name *
              </label>
              <Input
                id="productName"
                name="productName"
                value={formData.productName}
                onChange={handleChange}
                placeholder="e.g., Premium Ghee 500g"
                disabled={creating}
                error={validationErrors.productName}
              />
              {validationErrors.productName && (
                <p className="mt-1 text-sm text-red-600">{validationErrors.productName}</p>
              )}
            </div>

            {/* Batch Title Inputs */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="productCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Product Code *
                </label>
                <select
                  id="productCode"
                  name="productCode"
                  value={formData.productCode}
                  onChange={handleSelectChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={creating || batchTitleLoading}
                >
                  <option value="">Select product code</option>
                  {batchTitleOptions?.productCodes.map((option) => (
                    <option key={option.id} value={option.code}>
                      {option.code}{option.name ? ` - ${option.name}` : ''}
                    </option>
                  ))}
                </select>
                {validationErrors.productCode && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.productCode}</p>
                )}
              </div>

              <div>
                <label htmlFor="unitCode" className="block text-sm font-medium text-gray-700 mb-2">
                  Unit Code *
                </label>
                <select
                  id="unitCode"
                  name="unitCode"
                  value={formData.unitCode}
                  onChange={handleSelectChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={creating || batchTitleLoading}
                >
                  <option value="">Select unit code</option>
                  {batchTitleOptions?.unitCodes.map((option) => (
                    <option key={option.id} value={option.code}>
                      {option.code}{option.name ? ` - ${option.name}` : ''}
                    </option>
                  ))}
                </select>
                {validationErrors.unitCode && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.unitCode}</p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="processVersion" className="block text-sm font-medium text-gray-700 mb-2">
                  Process Version
                </label>
                <select
                  id="processVersion"
                  name="processVersion"
                  value={formData.processVersion}
                  onChange={handleSelectChange}
                  className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  disabled={creating || batchTitleLoading}
                >
                  <option value="">Select process version (optional)</option>
                  {batchTitleOptions?.processVersions.map((option) => (
                    <option key={option.id} value={option.code}>
                      {option.code}{option.name ? ` - ${option.name}` : ''}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label htmlFor="eventDate" className="block text-sm font-medium text-gray-700 mb-2">
                  Batch Date *
                </label>
                <Input
                  id="eventDate"
                  name="eventDate"
                  type="date"
                  value={formData.eventDate}
                  onChange={handleChange}
                  disabled={creating}
                  error={validationErrors.eventDate}
                />
                {validationErrors.eventDate && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.eventDate}</p>
                )}
                {!validationErrors.eventDate && (
                  <p className="mt-1 text-xs text-gray-500">
                    Use the date tied to this batch title. For fresh produce, use today&apos;s date or the harvest date.
                  </p>
                )}
              </div>
            </div>

            <div className="flex items-start gap-3">
              <input
                id="includeInnerQr"
                name="includeInnerQr"
                type="checkbox"
                checked={!!formData.includeInnerQr}
                onChange={handleCheckboxChange}
                disabled={creating}
                className="mt-1"
              />
              <div>
                <label htmlFor="includeInnerQr" className="text-sm font-medium text-gray-700">
                  Generate inner QR code
                </label>
                <p className="text-xs text-gray-500 mt-1">
                  Disable this for products where post-purchase consumption tracking is not needed.
                </p>
              </div>
            </div>

            {batchTitleError && (
              <p className="text-sm text-red-600">{batchTitleError}</p>
            )}
            <div className="flex justify-end">
              <button
                type="button"
                onClick={scrollToBatchTitleSettings}
                className="text-sm text-blue-600 hover:text-blue-700"
              >
                Manage batch title codes
              </button>
            </div>

            {/* Serial Number Range */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label htmlFor="serialStart" className="block text-sm font-medium text-gray-700 mb-2">
                  Starting Serial Number *
                </label>
                <Input
                  id="serialStart"
                  name="serialStart"
                  type="number"
                  min="1"
                  value={formData.serialStart}
                  onChange={handleChange}
                  placeholder="e.g., 1"
                  disabled={creating}
                  error={validationErrors.serialStart}
                />
                {validationErrors.serialStart && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.serialStart}</p>
                )}
              </div>

              <div>
                <label htmlFor="serialEnd" className="block text-sm font-medium text-gray-700 mb-2">
                  Ending Serial Number *
                </label>
                <Input
                  id="serialEnd"
                  name="serialEnd"
                  type="number"
                  min="1"
                  value={formData.serialEnd}
                  onChange={handleChange}
                  placeholder="e.g., 1000"
                  disabled={creating}
                  error={validationErrors.serialEnd}
                />
                {validationErrors.serialEnd && (
                  <p className="mt-1 text-sm text-red-600">{validationErrors.serialEnd}</p>
                )}
              </div>
            </div>

            {/* QR Code Settings */}
            <div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    QR ID format
                  </label>
                  <select
                    value={qrTokenFormat}
                    onChange={(e) => setQrTokenFormat(e.target.value as 'legacy' | 'short')}
                    className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    disabled={creating}
                  >
                    <option value="legacy">Standard (long ID, maximum compatibility)</option>
                    <option value="short">Compact (short ID, better for small labels)</option>
                  </select>
                </div>
                {qrTokenFormat === 'short' && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      QR ID length
                    </label>
                    <select
                      value={qrTokenLength}
                      onChange={(e) => setQrTokenLength(Number(e.target.value))}
                      className="px-4 py-2 border border-gray-300 rounded-lg bg-white text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      disabled={creating}
                    >
                      <option value={8}>8 characters (smallest)</option>
                      <option value={12}>12 characters</option>
                      <option value={16}>16 characters (recommended)</option>
                      <option value={20}>20 characters</option>
                      <option value={24}>24 characters</option>
                    </select>
                  </div>
                )}
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Short IDs reduce QR density and improve scan reliability on small prints.
              </p>
            </div>

            {/* Enhanced Information Toggle */}
            <BatchCreationStepHelp
              step={enhancedGuideStep}
              onOpenBatchManagement={scrollToBatchManagement}
              onOpenBatchTitleSettings={scrollToBatchTitleSettings}
              onOpenCreateBatchForm={scrollToCreateForm}
            />

            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Enhanced Product Information</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Add images, nutrition facts, packaging, and extra product details when this batch needs richer downstream data.
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => setShowEnhancedSections(!showEnhancedSections)}
                  className="flex items-center gap-2 px-4 py-2 text-sm font-medium text-blue-600 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors"
                  disabled={creating}
                >
                  {showEnhancedSections ? (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                      Hide Enhanced Options
                    </>
                  ) : (
                    <>
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                      </svg>
                      Add Enhanced Information
                    </>
                  )}
                </button>
              </div>
            </div>

            {/* Enhanced Information Sections */}
            {showEnhancedSections && (
              <div className="space-y-8 border-l-4 border-blue-200 pl-4 sm:pl-6 ml-0 sm:ml-4">
                {productType === 'produce' && (
                  <div className="space-y-3">
                    <h4 className="text-md font-medium text-gray-900">Produce details</h4>
                    <div className="rounded-lg border border-emerald-200 bg-emerald-50 p-4">
                      <p className="text-sm font-medium text-emerald-900">Popular produce presets</p>
                      <p className="mt-1 text-xs text-emerald-800">
                        Use a preset to set today&apos;s produce date, fill the variety, and prefill approximate nutrition per 100 g.
                      </p>
                      <div className="mt-3 flex flex-wrap gap-2">
                        {produceNutritionPresets.map((preset) => (
                          <button
                            key={preset.key}
                            type="button"
                            onClick={() => applyProducePreset(preset)}
                            disabled={creating}
                            className={`rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${
                              appliedProducePresetKey === preset.key
                                ? 'border-emerald-600 bg-emerald-600 text-white'
                                : 'border-emerald-300 bg-white text-emerald-800 hover:bg-emerald-100'
                            }`}
                          >
                            {preset.label}
                          </button>
                        ))}
                      </div>
                      {appliedProducePresetKey && (
                        <p className="mt-3 text-xs text-emerald-900">
                          {produceNutritionPresets.find((preset) => preset.key === appliedProducePresetKey)?.summary}
                        </p>
                      )}
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Harvest date</label>
                        <Input
                          type="date"
                          value={produceDetails.harvestDate || ''}
                          onChange={(e) => handleProduceDetailsChange('harvestDate', e.target.value)}
                          disabled={creating}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Origin / farm / region</label>
                        <Input
                          value={produceDetails.origin || ''}
                          onChange={(e) => handleProduceDetailsChange('origin', e.target.value)}
                          placeholder="e.g., Nashik, India"
                          disabled={creating}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Variety</label>
                        <Input
                          value={produceDetails.variety || ''}
                          onChange={(e) => handleProduceDetailsChange('variety', e.target.value)}
                          placeholder="e.g., Alphonso mango"
                          disabled={creating}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Storage / handling</label>
                        <Input
                          value={produceDetails.storageInstructions || ''}
                          onChange={(e) => handleProduceDetailsChange('storageInstructions', e.target.value)}
                          placeholder="e.g., Keep refrigerated, do not freeze"
                          disabled={creating}
                        />
                      </div>
                    </div>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Expiry / Best before date</label>
                        <Input
                          type="date"
                          value={produceDetails.expiryDate || ''}
                          onChange={(e) => handleProduceDetailsChange('expiryDate', e.target.value)}
                          disabled={creating}
                        />
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Shelf life (days)</label>
                        <Input
                          type="number"
                          min="1"
                          max="365"
                          value={produceDetails.bestBeforeDays ?? ''}
                          onChange={(e) => handleProduceDetailsChange('bestBeforeDays', e.target.value ? parseInt(e.target.value) : undefined)}
                          placeholder="e.g., 60 (~2 months)"
                          disabled={creating}
                        />
                      </div>
                    </div>
                  </div>
                )}

                {/* Product Images Section */}
                <div>
                  <h4 className="text-md font-medium text-gray-900 mb-4">Product Images</h4>
                  <ImageUpload
                    maxFiles={5}
                    maxFileSize={5 * 1024 * 1024} // 5MB
                    acceptedFormats={['image/jpeg', 'image/png', 'image/webp']}
                    onUpload={handleImageUpload}
                    onRemove={handleImageRemove}
                    disabled={creating}
                    images={productImages}
                    className="mb-4"
                  />
                  {validationErrors.images && (
                    <p className="text-sm text-red-600">{validationErrors.images}</p>
                  )}
                </div>

                {/* Nutrition Information Section */}
                {productType === 'produce' && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 p-4">
                    <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
                      <div>
                        <p className="text-sm font-medium text-blue-900">Produce nutrition shortcuts</p>
                        <p className="mt-1 text-xs text-blue-800">
                          Popular produce presets use a 100 g serving size so the values are easy to compare and edit.
                        </p>
                      </div>
                      {suggestedProducePreset && isNutritionInfoEmpty(nutritionInfo) && (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          onClick={() => applyProducePreset(suggestedProducePreset)}
                          disabled={creating}
                        >
                          Apply {suggestedProducePreset.label} nutrition
                        </Button>
                      )}
                    </div>
                  </div>
                )}
                <NutritionInformationForm
                  nutritionInfo={nutritionInfo}
                  onChange={handleNutritionChange}
                  disabled={creating}
                  errors={validationErrors}
                />

                {/* Custom Content Section */}
                <CustomContentForm
                  customContent={customContent}
                  onChange={handleCustomContentChange}
                  disabled={creating}
                  errors={validationErrors}
                />

                {/* Manufacturing Details Section */}
                {productType === 'manufactured' && (
                  <ManufacturingDetailsForm
                    manufacturingDetails={manufacturingDetails}
                    onChange={handleManufacturingDetailsChange}
                    disabled={creating}
                    errors={validationErrors}
                  />
                )}

                {/* Packaging Hierarchy Section */}
                <PackagingHierarchyForm
                  packagingData={packagingData}
                  onChange={handlePackagingDataChange}
                  disabled={creating}
                  errors={validationErrors}
                />
              </div>
            )}

            <BatchCreationStepHelp
              step={confidentialHelpStep}
              onOpenBatchManagement={scrollToBatchManagement}
              onOpenBatchTitleSettings={scrollToBatchTitleSettings}
              onOpenCreateBatchForm={scrollToCreateForm}
            />

            <div className="border-t border-gray-200 pt-6">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3">
                <div>
                  <h3 className="text-lg font-medium text-gray-900">Confidential Information</h3>
                  <p className="text-sm text-gray-500 mt-1">
                    Optional retailer and dealer data that will be published as a confidential release for this batch.
                  </p>
                </div>
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate('/api-access')}
                  disabled={creating}
                >
                  Manage Access
                </Button>
              </div>

              <div className="mt-4 rounded-lg border border-gray-200 bg-gray-50 p-4 space-y-4">
                {!canManageConfidential && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    Manufacturer admin access is required to publish confidential data. Leave this section empty if you only want to create the batch.
                  </div>
                )}

                {canManageConfidential && confidentialStatusLoading && (
                  <p className="text-sm text-gray-500">Checking confidential premium status...</p>
                )}

                {canManageConfidential && !confidentialStatusLoading && !confidentialAccessActive && (
                  <div className="rounded-lg border border-blue-200 bg-blue-50 px-4 py-3 text-sm text-blue-800">
                    Confidential premium access is inactive. Keep this section empty, or activate premium from API Access before creating the batch.
                  </div>
                )}

                {confidentialStatusError && (
                  <p className="text-sm text-red-600">{confidentialStatusError}</p>
                )}

                <div>
                  <h4 className="text-sm font-medium text-gray-900">Retailer payload</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    At least one retailer field is required when this section is used.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="retailerDiscount" className="block text-sm font-medium text-gray-700 mb-2">
                      Retailer discount
                    </label>
                    <Input
                      id="retailerDiscount"
                      name="retailerDiscount"
                      value={confidentialForm.retailerDiscount}
                      onChange={handleConfidentialChange}
                      placeholder="e.g., 8% off"
                      disabled={creating}
                    />
                  </div>

                  <div>
                    <label htmlFor="retailerPromoCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Retailer promo code
                    </label>
                    <Input
                      id="retailerPromoCode"
                      name="retailerPromoCode"
                      value={confidentialForm.retailerPromoCode}
                      onChange={handleConfidentialChange}
                      placeholder="e.g., RETAILER8"
                      disabled={creating}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="retailerInstruction" className="block text-sm font-medium text-gray-700 mb-2">
                    Retailer instruction
                  </label>
                  <textarea
                    id="retailerInstruction"
                    name="retailerInstruction"
                    value={confidentialForm.retailerInstruction}
                    onChange={handleConfidentialChange}
                    placeholder="Retailer-only instructions"
                    disabled={creating}
                    className="w-full min-h-[96px] rounded-lg border border-gray-300 px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div>
                  <h4 className="text-sm font-medium text-gray-900">Dealer payload</h4>
                  <p className="text-xs text-gray-500 mt-1">
                    Optional data for downstream dealers.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="dealerDiscount" className="block text-sm font-medium text-gray-700 mb-2">
                      Dealer discount
                    </label>
                    <Input
                      id="dealerDiscount"
                      name="dealerDiscount"
                      value={confidentialForm.dealerDiscount}
                      onChange={handleConfidentialChange}
                      placeholder="Dealer discount"
                      disabled={creating}
                    />
                  </div>

                  <div>
                    <label htmlFor="dealerPromoCode" className="block text-sm font-medium text-gray-700 mb-2">
                      Dealer promo code
                    </label>
                    <Input
                      id="dealerPromoCode"
                      name="dealerPromoCode"
                      value={confidentialForm.dealerPromoCode}
                      onChange={handleConfidentialChange}
                      placeholder="Dealer promo code"
                      disabled={creating}
                    />
                  </div>
                </div>

                <div>
                  <label htmlFor="dealerInstruction" className="block text-sm font-medium text-gray-700 mb-2">
                    Dealer instruction
                  </label>
                  <textarea
                    id="dealerInstruction"
                    name="dealerInstruction"
                    value={confidentialForm.dealerInstruction}
                    onChange={handleConfidentialChange}
                    placeholder="Dealer-only instructions"
                    disabled={creating}
                    className="w-full min-h-[96px] rounded-lg border border-gray-300 px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                  />
                </div>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label htmlFor="validUntil" className="block text-sm font-medium text-gray-700 mb-2">
                      Valid until
                    </label>
                    <Input
                      id="validUntil"
                      name="validUntil"
                      type="date"
                      value={confidentialForm.validUntil}
                      onChange={handleConfidentialChange}
                      disabled={creating}
                      error={validationErrors.confidentialValidUntil}
                    />
                    {validationErrors.confidentialValidUntil && (
                      <p className="mt-1 text-sm text-red-600">{validationErrors.confidentialValidUntil}</p>
                    )}
                  </div>

                  <div>
                    <label htmlFor="notes" className="block text-sm font-medium text-gray-700 mb-2">
                      Notes
                    </label>
                    <textarea
                      id="notes"
                      name="notes"
                      value={confidentialForm.notes}
                      onChange={handleConfidentialChange}
                      placeholder="Internal notes for this release"
                      disabled={creating}
                      className="w-full min-h-[96px] rounded-lg border border-gray-300 px-4 py-2 text-gray-900 focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                    />
                  </div>
                </div>

                {validationErrors.confidentialRetailer && (
                  <p className="text-sm text-red-600">{validationErrors.confidentialRetailer}</p>
                )}
                {validationErrors.confidential && (
                  <p className="text-sm text-red-600">{validationErrors.confidential}</p>
                )}
              </div>
            </div>

            {/* Product Count Display */}
            {productCount > 0 && (
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-gray-700">Total Products in Batch</p>
                    <p className="text-xs text-gray-500 mt-1">
                      Serial numbers: {formData.serialStart} to {formData.serialEnd}
                    </p>
                  </div>
                  <div className="text-left sm:text-right">
                    <p className="text-3xl font-bold text-blue-600">{productCount.toLocaleString()}</p>
                    <p className="text-xs text-gray-500 mt-1">products</p>
                  </div>
                </div>
              </div>
            )}

            {/* Cost Display */}
            {productCount > 0 && (
              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
                  <div>
                    <p className="text-sm font-medium text-blue-800">Batch Creation Cost</p>
                    {costCalculation && (
                      <p className="text-xs text-blue-600 mt-1">
                        Rate: {operationCostsService.formatCurrency(costCalculation.perUnitCost)} per product
                      </p>
                    )}
                    {costError && (
                      <p className="text-xs text-red-600 mt-1">{costError}</p>
                    )}
                  </div>
                  <div className="text-left sm:text-right">
                    {loadingCost ? (
                      <div className="flex items-center gap-2">
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-600"></div>
                        <span className="text-sm text-blue-600">Calculating...</span>
                      </div>
                    ) : costCalculation ? (
                      <>
                        <p className="text-2xl font-bold text-blue-800">
                          {operationCostsService.formatCurrency(costCalculation.totalCost)}
                        </p>
                        <p className="text-xs text-blue-600 mt-1">total cost</p>
                      </>
                    ) : (
                      <p className="text-sm text-gray-500">Cost unavailable</p>
                    )}
                  </div>
                </div>
                
                {/* Cost breakdown for large batches */}
                {costCalculation && productCount > 100 && (
                  <div className="mt-3 pt-3 border-t border-blue-200">
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-xs text-blue-700">
                      <div>
                        <span className="font-medium">Per Product:</span>
                        <span className="ml-1">{operationCostsService.formatCurrency(costCalculation.perUnitCost)}</span>
                      </div>
                      <div>
                        <span className="font-medium">Quantity:</span>
                        <span className="ml-1">{productCount.toLocaleString()}</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Credit Validation Error */}
            {validationErrors.credits && (
              <Alert variant="error">
                {validationErrors.credits}
              </Alert>
            )}

            {/* Estimated Time */}
            {productCount > 0 && (
              <div className="text-sm text-gray-600">
                <p>
                  <strong>Estimated generation time:</strong>{' '}
                  {productCount < 100 ? '< 1 minute' : 
                   productCount < 1000 ? '1-2 minutes' : 
                   productCount < 5000 ? '2-5 minutes' : '5-10 minutes'}
                </p>
              </div>
            )}

            <BatchCreationStepHelp
              step={postCreationGuideStep}
              onOpenBatchManagement={scrollToBatchManagement}
              onOpenBatchTitleSettings={scrollToBatchTitleSettings}
              onOpenCreateBatchForm={scrollToCreateForm}
            />
          </div>

          {/* Action Buttons */}
          <div className="mt-8 flex flex-col sm:flex-row sm:justify-end gap-3">
            <Button
              type="button"
              variant="outline"
              onClick={handleCancel}
              disabled={creating}
              className="w-full sm:w-auto"
            >
              Cancel
            </Button>
            <Button
              type="submit"
              disabled={creating || productCount === 0 || !hasCredits || !!validationErrors.credits || loadingCost}
              className="w-full sm:w-auto"
            >
              {creating ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Creating Batch...
                </>
              ) : (
                <>
                  <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                  </svg>
                  Create Batch
                </>
              )}
            </Button>
          </div>
          
          {/* Credit and Cost Info */}
          {!hasCredits && creditDetails && costCalculation && (
            <div className="mt-4 text-sm text-red-600 text-left sm:text-right">
              <p>
                Insufficient credits to create batch.
              </p>
              <p className="text-xs mt-1">
                Required: {operationCostsService.formatCurrency(costCalculation.totalCost)} | 
                Available: {operationCostsService.formatCurrency(creditDetails.available)} | 
                Need: {operationCostsService.formatCurrency(costCalculation.totalCost - creditDetails.available)} more
              </p>
            </div>
          )}
          
          {hasCredits && costCalculation && (
            <div className="mt-4 text-sm text-green-600 text-left sm:text-right">
              <p>✓ Sufficient credits available</p>
              <p className="text-xs mt-1">
                Cost: {operationCostsService.formatCurrency(costCalculation.totalCost)} | 
                Available: {operationCostsService.formatCurrency(creditDetails?.available || 0)}
              </p>
            </div>
          )}
        </form>
    </div>
  );
}
